import { type MouseEventHandler, type ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CustomFilterChip, RangeSliderFilterConfig, SliderFilterConfig } from '@alltrails/denali/components/FilterBar';
import { formatRatingValue } from '../components/AllFiltersModal/AllFiltersModal';
import type ExploreServiceFilters from '../types/ExploreServiceFilters';
import type TrailEngagement from '../types/TrailEngagement';
import getAppliedMultiSelectFilters, { AppliedFilterGroup, getAppliedFilterSortOrder } from '../utils/getAppliedMultiSelectFilters';
import { trailEngagementLabels, trailEngagementValues } from './filterConfigs/useTrailEngagementFilterConfig';

type RangeFilterConfig = RangeSliderFilterConfig | SliderFilterConfig;

type UseAppliedFilterPillsArgs = {
  filters: ExploreServiceFilters;
  // The range filter configs, reused for their summarized label + clear handler.
  rangeFilters: {
    length: RangeFilterConfig;
    elevationGain: RangeFilterConfig;
    highestPoint: RangeFilterConfig;
    duration: RangeFilterConfig;
    distanceAway: RangeFilterConfig;
  };
  trailEngagement: TrailEngagement[];
  onFiltersChange: (filters: ExploreServiceFilters) => void;
  onTrailEngagementChange: (trailEngagement: TrailEngagement[]) => void;
};

/**
 * Builds the "applied filter" pills shown in the revamped filter bar: one removable chip per applied value,
 * ordered to match the All Filters modal's section order. Multi-selects yield one pill per selected value
 * (its ✕ removes just that value); range filters reuse their config's summarized label + clear; rating and
 * my-trails pills are derived from state.
 */
export default function useAppliedFilterPills({
  filters,
  rangeFilters,
  trailEngagement,
  onFiltersChange,
  onTrailEngagementChange
}: UseAppliedFilterPillsArgs): CustomFilterChip[] {
  const intl = useIntl();
  const { length, elevationGain, highestPoint, duration, distanceAway } = rangeFilters;

  return useMemo(() => {
    type AppliedPill = { id: string; label: ReactNode; order: number; onClear?: MouseEventHandler<HTMLButtonElement> };
    const pills: AppliedPill[] = [];

    // Multi-select filters: one pill per selected value; its ✕ removes just that value.
    getAppliedMultiSelectFilters(filters, intl).forEach(appliedFilter => {
      pills.push({
        id: appliedFilter.id,
        label: appliedFilter.label,
        order: appliedFilter.order,
        onClear: () => {
          const newFilters = { ...filters };
          const remaining = ((newFilters[appliedFilter.filterKey] as string[] | undefined) ?? []).filter(value => value !== appliedFilter.value);
          if (remaining.length) {
            (newFilters as Record<string, unknown>)[appliedFilter.filterKey] = remaining;
          } else {
            delete newFilters[appliedFilter.filterKey];
          }
          onFiltersChange(newFilters);
        }
      });
    });

    // Range filters: one pill each, reusing the config's summarized label + clear.
    (
      [
        ['length', length],
        ['elevationGain', elevationGain],
        ['highestPoint', highestPoint],
        ['duration', duration],
        ['distanceAway', distanceAway]
      ] as [AppliedFilterGroup, RangeFilterConfig][]
    ).forEach(([group, config]) => {
      const selectedItems = config.filterChip.selectedItems;
      if (selectedItems && selectedItems.length > 0) {
        pills.push({ id: config.id, label: selectedItems[0], order: getAppliedFilterSortOrder(group), onClear: config.filterChip.onClear });
      }
    });

    // Rating: its config intentionally has no selectedItems, so derive the pill from state.
    if (filters.minRating) {
      pills.push({
        id: 'minRating',
        label: formatRatingValue(filters.minRating),
        order: getAppliedFilterSortOrder('minRating'),
        onClear: () => {
          const newFilters = { ...filters };
          delete newFilters.minRating;
          onFiltersChange(newFilters);
        }
      });
    }

    // My trails (trail engagement): one pill per selected value.
    trailEngagementValues.forEach((value, valueIndex) => {
      if (trailEngagement.includes(value)) {
        pills.push({
          id: `trailEngagement:${value}`,
          label: intl.formatMessage(trailEngagementLabels[value]),
          order: getAppliedFilterSortOrder('trailEngagement', valueIndex),
          onClear: () => onTrailEngagementChange(trailEngagement.filter(current => current !== value))
        });
      }
    });

    return pills
      .sort((a, b) => a.order - b.order)
      .map(pill => ({
        type: 'custom-chip' as const,
        id: `applied-${pill.id}`,
        filterChip: { selectedItems: [pill.label], unselectedText: pill.label, onClear: pill.onClear }
      }));
  }, [filters, intl, length, elevationGain, highestPoint, duration, distanceAway, trailEngagement, onFiltersChange, onTrailEngagementChange]);
}
